import { useState } from 'react';
import { Country } from '@tiendanube/common/src/enums';
import {
  PROJECT_SHIVA_TAG,
  IS_PAGO_NUBE_ONBOARDING_ON,
} from 'App/featuresFlags';
import { useHasTags, useNavegate, useIsBoldProject } from 'App/hooks';
import {
  trackingDashboardBoldClick,
  trackingPaymentOthersClick,
  trackingSeePreferencesNuvemPagoClick,
  trackingSeePreferencesPagoNubeClick,
} from 'App/tracking';
import { openWindow } from 'commons/utils/window';
import { useGetCountry, useLoggedAdminLink } from 'domains/Auth/hooks';
import useOnboardingTasks from 'domains/Dashboard/pages/DashboardPage/components/OnboardingTasks/useOnboardingTasks';
import dashboardService from 'domains/Dashboard/services';
import { useNuvemPagoFeatureFlag } from 'domains/NuvemPagoCommons/hooks/useNuvemPagoFeatureFlag';
import { NUVEM_PAGO_ONBOARDING_ROUTES } from '../../constants';
import { NuvemPagoOnboardingConfig } from '../../types';

export function useNuvemPagoOnboardingConfig() {
  const { goTo } = useNavegate();
  const { goToLoggedAdminLink } = useLoggedAdminLink();
  const isBoldProject = useIsBoldProject();
  const [isProjectShiva] = useHasTags([PROJECT_SHIVA_TAG]);
  const storeCountry = useGetCountry();
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const { isFeatureEnabled } = useNuvemPagoFeatureFlag(
    IS_PAGO_NUBE_ONBOARDING_ON,
  );
  const { getOnboarding } = useOnboardingTasks();

  const handleNuvemPagoOnboarding = async (
    config: NuvemPagoOnboardingConfig,
  ) => {
    const {
      url,
      isSecondaryLink,
      storeCountry: country,
      isProjectShiva: isShiva,
      isBoldProject: isBold,
    } = config;

    setIsLoadingPayments(true);

    try {
      // Handle secondary links for Shiva projects
      if (isShiva && isSecondaryLink) {
        await handleSecondaryLink(url, country);
        return;
      }

      // Handle settings URLs
      if (isShiva && url.startsWith(NUVEM_PAGO_ONBOARDING_ROUTES.settings)) {
        trackingSeePreferencesNuvemPagoClick();
        goTo(url);
        return;
      }

      // Handle payments settings
      if (url.startsWith('/settings/payments')) {
        handlePaymentsSettings(isBold);
        goTo(url);
        return;
      }

      // Handle other URLs
      await handleOtherUrls(url, isShiva);
      getOnboarding();
    } finally {
      setIsLoadingPayments(false);
    }
  };

  const handleSecondaryLink = async (url: string, country: string) => {
    const isCountryNeeded = [Country.BR, Country.AR].includes(
      country as Country,
    );

    if (isCountryNeeded) {
      goTo(url);
    } else {
      setIsLoadingPayments(false);
      openWindow(url, true);
    }
  };

  const handlePaymentsSettings = (isBold: boolean) => {
    if (isBold) {
      trackingDashboardBoldClick();
    } else {
      trackingPaymentOthersClick();
    }
  };

  const handleOtherUrls = async (url: string, isShiva: boolean) => {
    if (!isShiva) {
      await dashboardService.updateOnboardingTaskSetupPayments();
    }

    if (url.startsWith('/admin')) {
      trackingSeePreferencesNuvemPagoClick();
      const adminUrl = url.replace('/admin', '');
      goToLoggedAdminLink(adminUrl);
    } else {
      trackingSeePreferencesPagoNubeClick();
      goTo(url);
    }
  };

  const handleNuvemPagoEnable = (url: string) => {
    if (url.startsWith('/nuvempago')) {
      if (isFeatureEnabled) {
        return goTo(NUVEM_PAGO_ONBOARDING_ROUTES.onboarding);
      } else {
        return goTo(NUVEM_PAGO_ONBOARDING_ROUTES.pagoNubeOnboarding);
      }
    }
    return goTo(url);
  };

  const handleReviewClick = () => {
    goTo('/nuvempago#/settings');
  };

  return {
    handleNuvemPagoOnboarding,
    handleNuvemPagoEnable,
    handleReviewClick,
    isLoadingPayments,
    isProjectShiva,
    isBoldProject,
    storeCountry,
  };
}

export default useNuvemPagoOnboardingConfig;
